import React, { useState, forwardRef } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  Box,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Visibility, VisibilityOff, Clear } from '@mui/icons-material';
import { brandColors, designTokens } from '~/presentation/themes/modernTheme';

const StyledTextField = styled(TextField)(({ theme, variant, size, error, success }) => {
  const sizeStyles = {
    small: {
      '& .MuiInputBase-root': {
        height: 36,
        fontSize: '0.875rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
      },
    },
    medium: {
      '& .MuiInputBase-root': {
        height: 44,
        fontSize: '1rem',
      },
    },
    large: {
      '& .MuiInputBase-root': {
        height: 52,
        fontSize: '1rem',
      },
    },
  };

  return {
    '& .MuiInputLabel-root': {
      fontFamily: designTokens.typography.fontFamily,
      fontWeight: designTokens.typography.fontWeights.medium,
      color: brandColors.neutral[600],
      '&.Mui-focused': {
        color: success ? brandColors.success[500] : error ? brandColors.error[500] : brandColors.primary[500],
      },
    },
    
    '& .MuiOutlinedInput-root': {
      borderRadius: designTokens.borderRadius.md,
      backgroundColor: '#ffffff',
      fontFamily: designTokens.typography.fontFamily,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      
      '& fieldset': {
        borderColor: brandColors.neutral[300],
        borderWidth: 1,
      },
      
      '&:hover fieldset': {
        borderColor: success ? brandColors.success[300] : error ? brandColors.error[300] : brandColors.primary[300],
      },
      
      '&.Mui-focused fieldset': {
        borderColor: success ? brandColors.success[500] : error ? brandColors.error[500] : brandColors.primary[500],
        borderWidth: 2,
        boxShadow: `0 0 0 3px ${success ? brandColors.success[500] : error ? brandColors.error[500] : brandColors.primary[500]}20`,
      },
      
      '&.Mui-error fieldset': {
        borderColor: brandColors.error[500],
      },
      
      '&.Mui-disabled': {
        backgroundColor: brandColors.neutral[100],
        '& fieldset': {
          borderColor: brandColors.neutral[200],
        },
      },
    },
    
    '& .MuiInputBase-input': {
      padding: `${designTokens.spacing.md}px ${designTokens.spacing.md}px`,
      '&::placeholder': {
        color: brandColors.neutral[400],
        opacity: 1,
      },
    },
    
    '& .MuiFormHelperText-root': {
      fontFamily: designTokens.typography.fontFamily,
      fontSize: '0.75rem',
      marginTop: designTokens.spacing.xs,
      marginLeft: 0,
      '&.Mui-error': {
        color: brandColors.error[500],
      },
    },
    
    ...sizeStyles[size || 'medium'],
  };
});

const StyledFormControl = styled(FormControl)(({ theme, size, error, success }) => {
  const sizeStyles = {
    small: {
      '& .MuiInputBase-root': {
        height: 36,
        fontSize: '0.875rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
      },
    },
    medium: {
      '& .MuiInputBase-root': {
        height: 44,
        fontSize: '1rem',
      },
    },
    large: {
      '& .MuiInputBase-root': {
        height: 52,
        fontSize: '1rem',
      },
    },
  };

  return {
    '& .MuiInputLabel-root': {
      fontFamily: designTokens.typography.fontFamily,
      fontWeight: designTokens.typography.fontWeights.medium,
      color: brandColors.neutral[600],
      '&.Mui-focused': {
        color: success ? brandColors.success[500] : error ? brandColors.error[500] : brandColors.primary[500],
      },
    },
    
    '& .MuiOutlinedInput-root': {
      borderRadius: designTokens.borderRadius.md,
      backgroundColor: '#ffffff',
      fontFamily: designTokens.typography.fontFamily,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      
      '& fieldset': {
        borderColor: brandColors.neutral[300],
        borderWidth: 1,
      },
      
      '&:hover fieldset': {
        borderColor: success ? brandColors.success[300] : error ? brandColors.error[300] : brandColors.primary[300],
      },
      
      '&.Mui-focused fieldset': {
        borderColor: success ? brandColors.success[500] : error ? brandColors.error[500] : brandColors.primary[500],
        borderWidth: 2,
        boxShadow: `0 0 0 3px ${success ? brandColors.success[500] : error ? brandColors.error[500] : brandColors.primary[500]}20`,
      },
      
      '&.Mui-error fieldset': {
        borderColor: brandColors.error[500],
      },
      
      '&.Mui-disabled': {
        backgroundColor: brandColors.neutral[100],
        '& fieldset': {
          borderColor: brandColors.neutral[200],
        },
      },
    },
    
    '& .MuiInputBase-input': {
      padding: `${designTokens.spacing.md}px ${designTokens.spacing.md}px`,
      '&::placeholder': {
        color: brandColors.neutral[400],
        opacity: 1,
      },
    },
    
    '& .MuiFormHelperText-root': {
      fontFamily: designTokens.typography.fontFamily,
      fontSize: '0.75rem',
      marginTop: designTokens.spacing.xs,
      marginLeft: 0,
      '&.Mui-error': {
        color: brandColors.error[500],
      },
    },
    
    ...sizeStyles[size || 'medium'],
  };
});

const ModernInput = forwardRef(({
  label,
  placeholder,
  type = 'text',
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  success,
  helperText,
  disabled = false,
  required = false,
  fullWidth = true,
  size = 'medium',
  variant = 'outlined',
  startIcon,
  endIcon,
  clearable = false,
  onClear,
  multiline = false,
  rows = 4,
  maxRows,
  className,
  sx,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);
  
  const isPassword = type === 'password';
  const inputType = isPassword && showPassword ? 'text' : type;
  const hasValue = value && value.toString().length > 0;
  
  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };
  
  const handleClear = () => {
    if (onClear) {
      onClear();
    } else if (onChange) {
      onChange({ target: { value: '' } });
    }
  };
  
  const handleFocus = (e) => {
    setFocused(true);
    if (onFocus) onFocus(e);
  };
  
  const handleBlur = (e) => {
    setFocused(false);
    if (onBlur) onBlur(e);
  };
  
  const startAdornment = startIcon ? (
    <InputAdornment position="start">
      <Box sx={{ color: focused ? brandColors.primary[500] : brandColors.neutral[400], display: 'flex' }}>
        {startIcon}
      </Box>
    </InputAdornment>
  ) : null;
  
  const endAdornment = (startIcon || endIcon || isPassword || (clearable && hasValue)) ? (
    <InputAdornment position="end">
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {clearable && hasValue && !disabled && (
          <IconButton
            onClick={handleClear}
            size="small"
            sx={{
              color: brandColors.neutral[400],
              '&:hover': {
                color: brandColors.neutral[600],
                backgroundColor: brandColors.neutral[100],
              },
            }}
          >
            <Clear fontSize="small" />
          </IconButton>
        )}
        
        {isPassword && (
          <IconButton
            onClick={handlePasswordToggle}
            size="small"
            sx={{
              color: brandColors.neutral[400],
              '&:hover': {
                color: brandColors.neutral[600],
                backgroundColor: brandColors.neutral[100],
              },
            }}
          >
            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </IconButton>
        )}
        
        {endIcon && (
          <Box sx={{ color: focused ? brandColors.primary[500] : brandColors.neutral[400], display: 'flex' }}>
            {endIcon}
          </Box>
        )}
      </Box>
    </InputAdornment>
  ) : null;
  
  return (
    <StyledTextField
      ref={ref}
      label={label}
      placeholder={placeholder}
      type={inputType}
      value={value}
      onChange={onChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      error={!!error}
      success={success}
      helperText={error || helperText}
      disabled={disabled}
      required={required}
      fullWidth={fullWidth}
      size={size}
      variant={variant}
      multiline={multiline}
      rows={multiline ? rows : undefined}
      maxRows={maxRows}
      className={`modern-input ${className || ''}`}
      sx={sx}
      InputProps={{
        startAdornment,
        endAdornment,
      }}
      {...props}
    />
  );
});

// Componente de Input com label customizado
export const ModernInputWithLabel = ({
  label,
  required = false,
  description,
  children,
  error,
  className,
  sx,
  ...props
}) => {
  return (
    <Box className={`modern-input-with-label ${className || ''}`} sx={sx}>
      {label && (
        <Typography
          component="label"
          sx={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: designTokens.typography.fontWeights.medium,
            color: error ? brandColors.error[500] : brandColors.neutral[700],
            mb: 1,
            fontFamily: designTokens.typography.fontFamily,
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{
                color: brandColors.error[500],
                ml: 0.5,
              }}
            >
              *
            </Typography>
          )}
        </Typography>
      )}
      
      {description && (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: brandColors.neutral[600],
            mb: 1,
            fontFamily: designTokens.typography.fontFamily,
          }}
        >
          {description}
        </Typography>
      )}
      
      {children || <ModernInput {...props} error={error} />}
    </Box>
  );
};

// Componente de Input de busca
export const ModernSearchInput = ({
  placeholder = 'Buscar...',
  onSearch,
  debounceMs = 300,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [debounceTimer, setDebounceTimer] = useState(null);
  
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    const timer = setTimeout(() => {
      if (onSearch) {
        onSearch(value);
      }
    }, debounceMs);
    
    setDebounceTimer(timer);
  };
  
  return (
    <ModernInput
      placeholder={placeholder}
      value={searchValue}
      onChange={handleSearchChange}
      clearable
      onClear={() => {
        setSearchValue('');
        if (onSearch) onSearch('');
      }}
      startIcon={
        <Box sx={{ color: 'inherit' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
        </Box>
      }
      {...props}
    />
  );
};

ModernInput.displayName = 'ModernInput';

export default ModernInput;